/**
 * Map module registry — a decentralised factory pattern.
 *
 * Each concrete map module registers its constructor with the registry
 * so that {@link MapView.vue} does not need an explicit switch statement
 * listing every module type. Adding a new module only requires:
 *
 * 1. Creating the module class
 * 2. Calling `registerMapModule(type, Constructor)` at the bottom of the file
 * 3. Importing that file (for its side effect) in MapView.vue
 */
import { type AbstractMapModuleConfig } from "atlas-shared";
import { type ViewStackController } from "gertrud";
import type { AtlasMapModule } from "@/mapModules/AtlasMapModule";

/** Constructor signature shared by all map modules (generic over config subtype). */
type MapModuleConstructor<C extends AbstractMapModuleConfig = AbstractMapModuleConfig> = new (
    config: C,
    vsc: ViewStackController,
) => AtlasMapModule;

const registry = new Map<string, MapModuleConstructor<any>>();

/**
 * Register a map module class for a given `type` discriminator string.
 * The `type` must match the `type` literal in the corresponding config interface.
 * Accepts any constructor whose config parameter extends `AbstractMapModuleConfig`.
 */
export function registerMapModule<C extends AbstractMapModuleConfig>(
    type: string,
    ctor: MapModuleConstructor<C>,
): void {
    if (registry.has(type)) {
        console.warn(`[mapModuleRegistry] Overwriting existing registration for type "${type}"`);
    }
    registry.set(type, ctor);
}

/**
 * Instantiate a map module by its config's `type` field.
 *
 * @returns A new module instance, or `null` if no constructor is registered for `config.type`.
 */
export function createMapModule(
    config: AbstractMapModuleConfig,
    vsc: ViewStackController,
): AtlasMapModule | null {
    const ctor = registry.get(config.type);
    if (!ctor) {
        console.warn(`[mapModuleRegistry] No constructor registered for type "${config.type}"`);
        return null;
    }
    return new ctor(config, vsc);
}
